import { DEFAULT_TIMEZONE } from '@treneo/shared/constants';

import { useCourseViewModeStore } from './course-view-mode-store';
import { useCurrentUserQuery } from './current-user-query';

const selectUserDisplayNameFromInfo = (info: ReturnType<typeof useCurrentUserQuery>['data']) => {
  if (!info) {
    return null;
  }
  if (info.displayName) {
    return info.displayName;
  }
  const firstName = info.firstName ?? '';
  const lastName = info.lastName ?? '';
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) {
    return firstName;
  }
  if (lastName) {
    return lastName;
  }
  return null;
};

export const useSelectCurrentUserInfo = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data ?? null;
};

export const useSelectUserDisplayName = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return selectUserDisplayNameFromInfo(data) ?? null;
};

export const useSelectUserEmail = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data?.email ?? null;
};

export const useSelectUserAvatar = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data?.avatar ?? null;
};

export const useSelectUserAvatarColor = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data?.avatarColor ?? null;
};

export const useSelectUserTimezone = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  if (data?.timezone && data.timezone !== 'UTC') {
    return data.timezone;
  }
  return DEFAULT_TIMEZONE;
};

export const useSelectIsStartBannerHidden = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data?.isStartBannerHidden ?? false;
};

export const useSelectIsMachineBarPinned = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data?.isMachineBarPinned ?? false;
};

export const useSelectAreTipsHidden = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data?.areTipsHidden ?? false;
};

export const useSelectIsSaveCurrentUserInfoSuccess = () => false;

export const useSelectHasUserInfo = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return !!(data && Object.keys(data).length > 0);
};

export const useSelectUserInitials = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  if (!data) {
    return '';
  }
  const firstName = data.firstName?.charAt(0) ?? '';
  const lastName = data.lastName?.charAt(0) ?? '';
  return `${firstName}${lastName}`.toUpperCase();
};

export const useSelectIsStartBannerVisible = () => {
  const hidden = useSelectIsStartBannerHidden();
  return !hidden;
};

export const useSelectIsMachineBarUnpinned = () => {
  const isPinned = useSelectIsMachineBarPinned();
  return !isPinned;
};

export const useSelectAreTipsVisible = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return !data?.areTipsHidden;
};

export const useSelectIsUserInfoSaveSuccessful = () => false;

export const useSelectShouldShowWelcomeMessage = () => {
  const hasUserInfo = useSelectHasUserInfo();
  const isStartBannerVisible = useSelectIsStartBannerVisible();
  return hasUserInfo && isStartBannerVisible;
};

export const useSelectCanShowTips = () => {
  const hasUserInfo = useSelectHasUserInfo();
  const areTipsVisible = useSelectAreTipsVisible();
  return hasUserInfo && areTipsVisible;
};

export const useSelectIsUserSettingsComplete = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return !!(data?.firstName && data?.lastName && data?.displayName && data?.languageCode);
};

export const useSelectUserTheme = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data?.theme ?? null;
};

export const useSelectUserLanguage = () => {
  const { data } = useCurrentUserQuery({ enabled: true });
  return data?.languageCode ?? null;
};

export const useSelectCoursePreferences = () => {
  const viewMode = useCourseViewModeStore(state => state.viewMode);
  return { viewMode };
};

export const useSelectCourseViewMode = () => useCourseViewModeStore(state => state.viewMode);

export const useSelectIsGetCurrentUserInfoLoading = () => {
  const { isFetching } = useCurrentUserQuery({ enabled: true });
  return isFetching;
};

export const useSelectIsUserDarkTheme = () => {
  const theme = useSelectUserTheme();
  return theme === 'dark';
};

export const useSelectIsUserLightTheme = () => {
  const theme = useSelectUserTheme();
  return theme === 'light';
};
